import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, CardHeader, Chip } from '@heroui/react';
import { MapPin, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import persistentGoogleMapsLoader from '../../utils/persistentGoogleMapsLoader';

const SecurityMap = ({ posts = [], onPostClick }) => {
  const { user } = useAuth();
  const [mapState, setMapState] = useState({
    isLoaded: false,
    isLoading: false,
    error: null
  });
  
  // Use refs to store map-related objects to avoid React state conflicts
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const userLocationRef = useRef(null);
  const mapContainerRef = useRef(null);
  const visibleContainerRef = useRef(null);
  const initializationRef = useRef(false);
  
  // Attach map container to visible DOM when component mounts
  useEffect(() => {
    if (visibleContainerRef.current && !mapContainerRef.current) {
      console.log('SecurityMap: Creating map container in visible DOM...');
      
      // Clear any existing content
      visibleContainerRef.current.innerHTML = '';
      
      // Create the map container directly in the visible DOM
      const mapDiv = document.createElement('div');
      mapDiv.id = 'google-maps-container';
      mapDiv.style.width = '100%';
      mapDiv.style.height = '100%';
      
      visibleContainerRef.current.appendChild(mapDiv);
      mapContainerRef.current = mapDiv;
      
      console.log('SecurityMap: Map container attached to visible DOM');
    }
  }, [mapState.isLoaded]); // Run when map state changes
  
  // Single initialization effect - runs only once per mount
  useEffect(() => {
    if (initializationRef.current) return;
    initializationRef.current = true;
    
    console.log('SecurityMap: Starting initialization...');
    
    const initializeMap = async () => {
      try {
        console.log('SecurityMap: Loading Google Maps via persistent loader...');
        setMapState(prev => ({ ...prev, isLoading: true }));
        
        await persistentGoogleMapsLoader.load();
        
        console.log('SecurityMap: Google Maps loaded successfully');
        setMapState(prev => ({ ...prev, isLoaded: true, isLoading: false }));
        
        // Get user location and create map
        handleGoogleMapsReady();
        
      } catch (error) {
        console.error('SecurityMap: Failed to load Google Maps:', error);
        setMapState(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: error.message 
        }));
      }
    };
    
    const handleGoogleMapsReady = () => {
      console.log('SecurityMap: Google Maps ready, getting location...');
      
      // Get user location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            console.log('SecurityMap: Got user location:', location);
            userLocationRef.current = location;
            createMap(location);
          },
          (error) => {
            console.log('SecurityMap: Geolocation failed, using default location');
            const defaultLocation = { lat: -23.5505, lng: -46.6333 };
            userLocationRef.current = defaultLocation;
            createMap(defaultLocation);
          }
        );
      } else {
        console.log('SecurityMap: Geolocation not supported, using default location');
        const defaultLocation = { lat: -23.5505, lng: -46.6333 };
        userLocationRef.current = defaultLocation;
        createMap(defaultLocation);
      }
    };
    
    const createMap = (userLocation) => {
      console.log('SecurityMap: createMap called with:', {
        hasContainer: !!mapContainerRef.current,
        hasGoogle: !!(window.google && window.google.maps),
        userLocation
      });
      
      if (!mapContainerRef.current) {
        console.log('SecurityMap: Container ref not ready, retrying...');
        setTimeout(() => createMap(userLocation), 100);
        return;
      }
      
      if (!window.google || !window.google.maps) {
        console.log('SecurityMap: Google Maps not ready, retrying...');
        setTimeout(() => createMap(userLocation), 500);
        return;
      }
      
      try {
        console.log('SecurityMap: Creating map instance on portal container...');
        
        // Create map directly on the portal container - completely isolated from React
        const container = mapContainerRef.current;
        console.log('SecurityMap: Container element:', container);
        
        console.log('SecurityMap: Creating Google Map...');
        
        // Create map directly on the container - Google Maps takes full control
        const googleMap = new window.google.maps.Map(container, {
          center: userLocation,
          zoom: 13,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          disableDefaultUI: false,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });
        
        console.log('SecurityMap: Map created successfully');
        mapRef.current = googleMap;
        
        // Wait for map to be fully initialized before adding markers
        window.google.maps.event.addListenerOnce(googleMap, 'idle', () => {
          console.log('SecurityMap: Map is idle, creating markers...');
          updateMarkers();
        });
        
      } catch (error) {
        console.error('SecurityMap: Error creating map:', error);
        setMapState(prev => ({ 
          ...prev, 
          error: 'Error creating map: ' + error.message 
        }));
      }
    };
    
    initializeMap();
    
    // Cleanup function - minimal cleanup to prevent DOM conflicts
    return () => {
      console.log('SecurityMap: Starting cleanup...');
      
      // Clear markers using Google Maps API
      if (markersRef.current && markersRef.current.length > 0) {
        markersRef.current.forEach((marker, index) => {
          try {
            if (marker && typeof marker.setMap === 'function') {
              marker.setMap(null);
            }
          } catch (error) {
            console.warn(`SecurityMap: Error clearing marker ${index}:`, error);
          }
        });
        markersRef.current = [];
      }
      
      // Clear map reference (don't touch DOM)
      if (mapRef.current) {
        mapRef.current = null;
      }
      
      console.log('SecurityMap: Cleanup complete');
    };
  }, []); // Empty dependency array - runs only once per mount
  
  // Function to clear all markers - use direct Google Maps API
  const clearMarkers = () => {
    if (markersRef.current && markersRef.current.length > 0) {
      console.log('SecurityMap: Clearing', markersRef.current.length, 'markers');
      markersRef.current.forEach((marker, index) => {
        try {
          if (marker && typeof marker.setMap === 'function') {
            marker.setMap(null);
          }
        } catch (error) {
          console.warn(`SecurityMap: Error clearing marker ${index}:`, error);
        }
      });
      markersRef.current = [];
    }
  };
  
  // Function to update markers - pure Google Maps API approach
  const updateMarkers = () => {
    if (!mapRef.current || !userLocationRef.current || !window.google || !window.google.maps) {
      console.log('SecurityMap: Cannot update markers - missing dependencies');
      return;
    }
    
    try {
      console.log('SecurityMap: Updating markers for', posts.length, 'posts');
      
      // Clear existing markers first
      clearMarkers();
      
      const newMarkers = [];
      
      // Add user location marker using pure Google Maps API
      try {
        const userMarker = new window.google.maps.Marker({
          position: userLocationRef.current,
          map: mapRef.current,
          title: 'Sua Localização',
          zIndex: 1000,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="14" fill="#3b82f6" stroke="#ffffff" stroke-width="4"/>
                <circle cx="16" cy="16" r="6" fill="#ffffff"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(32, 32),
            anchor: new window.google.maps.Point(16, 16)
          }
        });
        
        newMarkers.push(userMarker);
        console.log('SecurityMap: Created user location marker');
      } catch (error) {
        console.error('SecurityMap: Error creating user marker:', error);
      }
      
      // Add post markers using pure Google Maps API
      posts.forEach((post, index) => {
        if (post.location && post.location.length >= 2) {
          try {
            const postLocation = {
              lat: post.location[1],
              lng: post.location[0]
            };
            
            const postDate = new Date(post.created_at);
            const now = new Date();
            const ageInDays = (now - postDate) / (1000 * 60 * 60 * 24);
            const opacity = ageInDays > 1 ? 0.6 : 1.0;
            
            const marker = new window.google.maps.Marker({
              position: postLocation,
              map: mapRef.current,
              title: `${post.title}\n${postDate.toLocaleDateString('pt-BR')}`,
              opacity: opacity,
              zIndex: 100,
              icon: {
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 11C24 18 14 26 14 26S4 18 4 11C4 6.02944 8.02944 2 14 2C19.9706 2 24 6.02944 24 11Z" fill="#dc2626" stroke="#ffffff" stroke-width="2"/>
                    <path d="M18 11C18 13.2091 16.2091 15 14 15C11.7909 15 10 13.2091 10 11C10 8.79086 11.7909 7 14 7C16.2091 7 18 8.79086 18 11Z" fill="#ffffff"/>
                    <circle cx="14" cy="11" r="2" fill="#dc2626"/>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(28, 28),
                anchor: new window.google.maps.Point(14, 26)
              }
            });
            
            // Add click listener using Google Maps API
            marker.addListener('click', () => {
              if (onPostClick) {
                onPostClick(post);
              }
            });
            
            newMarkers.push(marker);
            console.log(`SecurityMap: Created marker ${index + 1} for post: ${post.title?.substring(0, 30)}`);
          } catch (error) {
            console.error(`SecurityMap: Error creating marker for post ${index}:`, error);
          }
        }
      });
      
      // Store markers in ref (not React state)
      markersRef.current = newMarkers;
      
      // Adjust map bounds using Google Maps API
      if (newMarkers.length > 1) {
        try {
          const bounds = new window.google.maps.LatLngBounds();
          newMarkers.forEach(marker => {
            if (marker && marker.getPosition) {
              bounds.extend(marker.getPosition());
            }
          });
          
          mapRef.current.fitBounds(bounds);
          
          // Set minimum zoom level
          window.google.maps.event.addListenerOnce(mapRef.current, 'bounds_changed', () => {
            if (mapRef.current.getZoom() > 15) {
              mapRef.current.setZoom(15);
            }
          });
        } catch (error) {
          console.error('SecurityMap: Error adjusting map bounds:', error);
        }
      }
      
      console.log('SecurityMap: Successfully created', newMarkers.length, 'markers');
      
    } catch (error) {
      console.error('SecurityMap: Error in updateMarkers:', error);
    }
  };
  
  // Update markers when posts change
  useEffect(() => {
    if (mapRef.current && userLocationRef.current) {
      updateMarkers();
    }
  }, [posts, user?.avatar]);

  // Get recent posts count
  const getRecentPostsCount = () => {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    return posts.filter(post => new Date(post.created_at) > oneDayAgo).length;
  };

  const postsWithLocation = posts.filter(post => post.location && post.location.length >= 2);

  if (posts.length === 0) {
    console.log('SecurityMap: No posts provided, not rendering map');
    return null;
  }

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                Mapa de Segurança
              </h3>
              <p className="text-sm text-gray-600">
                Incidentes reportados na sua região
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Chip 
              color="danger" 
              variant="flat" 
              size="sm"
              startContent={<MapPin className="w-3 h-3" />}
            >
              {posts.length} incidente{posts.length !== 1 ? 's' : ''}
            </Chip>
            {getRecentPostsCount() > 0 && (
              <Chip 
                color="warning" 
                variant="flat" 
                size="sm"
              >
                {getRecentPostsCount()} recente{getRecentPostsCount() !== 1 ? 's' : ''}
              </Chip>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardBody className="pt-0">
        {mapState.error === 'fallback' ? (
          // Fallback list view
          <div className="h-80 bg-gray-50 rounded-lg p-4 overflow-y-auto">
            <div className="text-center mb-4">
              <MapPin className="w-8 h-8 text-gray-500 mx-auto mb-2" />
              <p className="text-gray-700 font-medium">Mapa indisponível</p>
              <p className="text-gray-500 text-sm">Mostrando lista de incidentes</p>
            </div>
            
            {postsWithLocation.length > 0 ? (
              <div className="space-y-3">
                {postsWithLocation.map((post, index) => (
                  <div 
                    key={post._id?.['$oid'] || post._id || index}
                    className="bg-white rounded-lg p-3 border border-gray-200 hover:border-red-300 cursor-pointer transition-colors"
                    onClick={() => onPostClick && onPostClick(post)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <AlertTriangle className="w-3 h-3 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">
                          {post.title}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {new Date(post.created_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {post.location && post.location.length >= 2 && (
                          <p className="text-xs text-gray-500 mt-1 font-mono">
                            📍 {post.location[1].toFixed(4)}, {post.location[0].toFixed(4)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500">
                <p>Nenhum incidente com localização encontrado</p>
              </div>
            )}
          </div>
        ) : mapState.error ? (
          <div className="flex items-center justify-center h-80 bg-red-50 rounded-lg border-red-200">
            <div className="text-center">
              <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-600 font-medium">Erro ao carregar o mapa</p>
              <p className="text-red-500 text-sm">{mapState.error}</p>
              <button 
                onClick={() => {
                  setMapState({
                    isLoaded: false,
                    isLoading: false,
                    error: null
                  });
                  initializationRef.current = false;
                }}
                className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        ) : (!mapState.isLoaded || mapState.isLoading) ? (
          <div className="flex items-center justify-center h-80 bg-gray-100 rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-2"></div>
              <p className="text-gray-600">
                {mapState.isLoading ? 'Carregando mapa de segurança...' : 'Inicializando mapa...'}
              </p>
            </div>
          </div>
        ) : (
          // Map container - directly in DOM, not portal
          <div 
            ref={visibleContainerRef}
            className="w-full h-80 rounded-lg border"
            style={{ 
              minHeight: '320px',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {!mapRef.current && (
              <div className="flex items-center justify-center h-full bg-yellow-50 rounded-lg border-yellow-200">
                <div className="text-center">
                  <div className="animate-pulse w-8 h-8 bg-yellow-400 rounded-full mx-auto mb-2"></div>
                  <p className="text-yellow-700">Criando mapa...</p>
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="mt-3 text-xs text-gray-500 text-center">
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-1"></div>
              <span>Sua localização</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-600 rounded-full mr-1"></div>
              <span>Incidentes reportados</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-300 rounded-full mr-1"></div>
              <span>Incidentes antigos (>24h)</span>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default SecurityMap;
